import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, Upload, X, Image, Package, Tag, Percent, Gift, ClipboardList } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'gold',
    jewelry_type: '',
    collection: '',
    image_url: '',
    metal_type: '',
    weight: '',
    weight_grams: '',
    making_charges: '',
    size_guide: '',
    selectedKarat: '22K',
    discount_percent: '',
    discount_active: false,
  });

  // Gold Rate State
  const [goldRate, setGoldRate] = useState(null);
  const [newRate24k, setNewRate24k] = useState('');
  const [makingChargePerGram, setMakingChargePerGram] = useState('');
  const [rateUpdating, setRateUpdating] = useState(false);
  const [descLoading, setDescLoading] = useState(false);
  const [autoBreakdown, setAutoBreakdown] = useState(null);

  // Collections State
  const [collections, setCollections] = useState([]);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [editingCollection, setEditingCollection] = useState(null);
  const [editCollectionName, setEditCollectionName] = useState('');

  // Coupons State
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: '', discount_type: 'percent', discount_value: '', min_order: '', max_discount: '' });

  // Sales State
  const [sales, setSales] = useState([]);
  const [saleForm, setSaleForm] = useState({ name: '', target_type: 'collection', target_value: '', discount_percent: '' });

  // Admin Orders State
  const [adminOrders, setAdminOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('all'); // all | gift | pending

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/');
      toast.error('Admin access only');
      return;
    }
    fetchProducts();
    fetchGoldRate();
    fetchCollections();
    fetchCoupons();
    fetchSales();
    fetchAdminOrders();
  }, [user]);

  const fetchCollections = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/collections`, { withCredentials: true });
      setCollections(data);
    } catch (error) { console.error('Error fetching collections'); }
  };

  const fetchCoupons = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/coupons`, { withCredentials: true });
      setCoupons(data);
    } catch (error) { console.error('Error fetching coupons'); }
  };

  const fetchSales = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/sales`, { withCredentials: true });
      setSales(data);
    } catch (error) { console.error('Error fetching sales'); }
  };

  const fetchAdminOrders = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/orders`, { withCredentials: true });
      setAdminOrders(data);
    } catch (error) { console.error('Error fetching orders'); }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/orders/${orderId}/status`,
        { status: newStatus }, { withCredentials: true });
      toast.success(`Order marked as ${newStatus}`);
      fetchAdminOrders();
    } catch (error) { toast.error('Failed to update order status'); }
  };

  const addCoupon = async () => {
    if (!couponForm.code || !couponForm.discount_value) { toast.error('Fill code and discount value'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/coupons`, {
        ...couponForm,
        discount_value: parseFloat(couponForm.discount_value),
        min_order: couponForm.min_order ? parseFloat(couponForm.min_order) : 0,
        max_discount: couponForm.max_discount ? parseFloat(couponForm.max_discount) : null,
      }, { withCredentials: true });
      toast.success('Coupon created!');
      setCouponForm({ code: '', discount_type: 'percent', discount_value: '', min_order: '', max_discount: '' });
      fetchCoupons();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create coupon'); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/coupons/${id}`, { withCredentials: true }); toast.success('Coupon deleted'); fetchCoupons(); }
    catch { toast.error('Failed to delete'); }
  };

  const addSale = async (overrides = {}) => {
    const finalForm = { ...saleForm, ...overrides };
    if (!finalForm.name || !finalForm.target_value || !finalForm.discount_percent) { toast.error('Fill all sale fields'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/sales`, {
        ...finalForm,
        discount_percent: parseFloat(finalForm.discount_percent),
      }, { withCredentials: true });
      toast.success('Sale created!');
      setSaleForm({ name: '', target_type: 'collection', target_value: '', discount_percent: '' });
      fetchSales();
    } catch (error) { toast.error('Failed to create sale'); }
  };

  const deleteSale = async (id) => {
    if (!window.confirm('Delete this sale?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/sales/${id}`, { withCredentials: true }); toast.success('Sale deleted'); fetchSales(); }
    catch { toast.error('Failed to delete'); }
  };

  const addCollection = async () => {
    if (!newCollectionName.trim()) { toast.error('Enter collection name'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/collections`, { name: newCollectionName.trim() }, { withCredentials: true });
      toast.success('Collection added!');
      setNewCollectionName('');
      fetchCollections();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add collection'); }
  };

  const updateCollection = async (id) => {
    if (!editCollectionName.trim()) { toast.error('Enter collection name'); return; }
    try {
      await axios.put(`${BACKEND_URL}/api/collections/${id}`, { name: editCollectionName.trim() }, { withCredentials: true });
      toast.success('Collection updated!');
      setEditingCollection(null);
      setEditCollectionName('');
      fetchCollections();
    } catch (error) { toast.error('Failed to update collection'); }
  };

  const deleteCollection = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/collections/${id}`, { withCredentials: true });
      toast.success('Collection deleted');
      fetchCollections();
    } catch (error) { toast.error('Failed to delete collection'); }
  };

  const fetchGoldRate = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/gold-rate`, { withCredentials: true });
      setGoldRate(data);
      setNewRate24k(String(data.rate_24k));
      setMakingChargePerGram(String(data.making_charge_per_gram || 0));
    } catch (error) { console.error('Error fetching gold rate'); }
  };

  const updateGoldRate = async () => {
    if (!newRate24k || isNaN(parseFloat(newRate24k))) {
      toast.error('Please enter valid rate');
      return;
    }
    setRateUpdating(true);
    try {
      const payload = { rate_24k: parseFloat(newRate24k) };
      if (makingChargePerGram && !isNaN(parseFloat(makingChargePerGram))) {
        payload.making_charge_per_gram = parseFloat(makingChargePerGram);
      }
      const { data } = await axios.post(`${BACKEND_URL}/api/gold-rate`, payload, { withCredentials: true });
      setGoldRate(data);
      toast.success('Gold rate & making charge updated!');
    } catch (error) { toast.error('Failed to update gold rate'); }
    finally { setRateUpdating(false); }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/products`, {
        withCredentials: true,
      });
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const { data } = await axios.post(`${BACKEND_URL}/api/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const imageUrl = `${BACKEND_URL}${data.url}`;
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'gold',
      jewelry_type: '',
      collection: '',
      image_url: '',
      metal_type: '',
      weight: '',
      weight_grams: '',
      making_charges: '',
      size_guide: '',
      selectedKarat: '22K',
      discount_percent: '',
      discount_active: false,
      available_date: '',
      in_stock: true,
    });
    setEditingProduct(null);
    setAutoBreakdown(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: String(product.price),
      category: product.category,
      jewelry_type: product.jewelry_type || '',
      collection: product.collection || '',
      image_url: product.image_url,
      metal_type: product.metal_type,
      weight: product.weight || '',
      weight_grams: String(product.weight_grams || ''),
      making_charges: String(product.making_charges || ''),
      size_guide: product.size_guide || '',
      selectedKarat: '22K',
      discount_percent: product.discount_percent ? String(product.discount_percent) : '',
      discount_active: product.discount_active || false,
      available_date: product.available_date || '',
      in_stock: product.in_stock !== false,
    });
    setAutoBreakdown(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      weight_grams: formData.weight_grams ? parseFloat(formData.weight_grams) : null,
      making_charges: formData.making_charges ? parseFloat(formData.making_charges) : 0,
      jewelry_type: formData.jewelry_type || null,
      collection: formData.collection || null,
      discount_percent: formData.discount_percent ? parseFloat(formData.discount_percent) : 0,
      discount_active: !!formData.discount_active,
      available_date: formData.available_date || null,
      in_stock: formData.in_stock !== false,
    };
    delete payload.selectedKarat;

    try {
      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/products/${editingProduct.id}`, payload, {
          withCredentials: true,
        });
        toast.success('Product updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/products`, payload, {
          withCredentials: true,
        });
        toast.success('Product added!');
      }

      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/products/${productId}`, {
        withCredentials: true,
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C27054] mx-auto mb-4"></div>
          <p className="text-[#7A8A73]">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-6" data-testid="admin-panel-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-2">
              Admin Dashboard
            </p>
            <h1 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1]">
              Manage Products
            </h1>
          </div>

          <button
            onClick={openAddModal}
            data-testid="add-product-button"
            className="bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold flex items-center gap-2"
          >
            <Plus size={18} />
            Add Product
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6">
            <p className="text-xs uppercase tracking-widest text-[#7A8A73] mb-1">Total Products</p>
            <p className="text-3xl font-serif text-[#2D3B26]" data-testid="total-products-count">
              {products.length}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6">
            <p className="text-xs uppercase tracking-widest text-[#7A8A73] mb-1">Gold Products</p>
            <p className="text-3xl font-serif text-[#2D3B26]">
              {products.filter((p) => p.category === 'gold').length}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6">
            <p className="text-xs uppercase tracking-widest text-[#7A8A73] mb-1">Silver Products</p>
            <p className="text-3xl font-serif text-[#2D3B26]">
              {products.filter((p) => p.category === 'silver').length}
            </p>
          </div>
        </div>

        {/* Gold Rate Management */}
        <div className="bg-white rounded-2xl p-6 mb-8" data-testid="gold-rate-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-serif text-[#2D3B26]">Today's Gold Rate & Making Charge</h2>
              <p className="text-xs text-[#7A8A73]">Set once - auto-applies to all products based on weight</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold text-[#2D3B26] uppercase tracking-wider mb-1">24K Rate (per gram)</label>
              <input
                type="number"
                value={newRate24k}
                onChange={(e) => setNewRate24k(e.target.value)}
                data-testid="input-gold-rate"
                className="w-40 px-4 py-2 bg-[#E4E8DF] border-none rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                placeholder="7500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#2D3B26] uppercase tracking-wider mb-1">Making Charge (per gram)</label>
              <input
                type="number"
                value={makingChargePerGram}
                onChange={(e) => setMakingChargePerGram(e.target.value)}
                data-testid="input-making-charge-per-gram"
                className="w-40 px-4 py-2 bg-[#E4E8DF] border-none rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                placeholder="500"
              />
            </div>
            <button
              onClick={updateGoldRate}
              disabled={rateUpdating}
              data-testid="update-rate-button"
              className="px-6 py-2 bg-[#C27054] text-white rounded-lg hover:bg-[#A85B42] transition-colors text-sm font-semibold disabled:opacity-50"
            >
              {rateUpdating ? 'Updating...' : 'Update'}
            </button>
          </div>
          {goldRate && (
            <div className="mt-4">
              <div className="flex gap-3 flex-wrap">
                {Object.entries(goldRate.rates || {}).map(([k, v]) => (
                  <div key={k} className="bg-[#f6f7f4] rounded-lg px-3 py-2 text-center">
                    <p className="text-xs font-bold text-[#2D3B26]">{k}</p>
                    <p className="text-xs text-[#C27054]">{'\u20b9'}{v.toLocaleString('en-IN')}/g</p>
                  </div>
                ))}
                <div className="bg-[#C27054]/10 rounded-lg px-3 py-2 text-center">
                  <p className="text-xs font-bold text-[#2D3B26]">Making</p>
                  <p className="text-xs text-[#C27054]">{'\u20b9'}{goldRate.making_charge_per_gram || 0}/g</p>
                </div>
              </div>
              <p className="text-[10px] text-[#7A8A73] mt-3">Last updated: {new Date(goldRate.updated_at).toLocaleString('en-IN')}</p>
            </div>
          )}
        </div>

        {/* Collection Manager */}
        <div className="bg-white rounded-2xl p-6 mb-8" data-testid="collection-manager">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-serif text-[#2D3B26]">Manage Collections</h2>
              <p className="text-xs text-[#7A8A73]">Add, edit or delete collections. These appear in product form dropdown.</p>
            </div>
          </div>

          {/* Add New Collection */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCollection()}
              data-testid="input-new-collection"
              className="flex-1 px-4 py-2 bg-[#E4E8DF] border-none rounded-lg text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              placeholder="Enter new collection name..."
            />
            <button
              onClick={addCollection}
              data-testid="add-collection-button"
              className="px-6 py-2 bg-[#C27054] text-white rounded-lg hover:bg-[#A85B42] transition-colors text-sm font-semibold"
            >
              Add
            </button>
          </div>

          {/* Collections List */}
          <div className="flex flex-wrap gap-2">
            {collections.map((col) => (
              <div key={col.id} className="group relative" data-testid={`collection-${col.id}`}>
                {editingCollection === col.id ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={editCollectionName}
                      onChange={(e) => setEditCollectionName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && updateCollection(col.id)}
                      className="px-3 py-1.5 bg-[#E4E8DF] border-none rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-xs w-32"
                      autoFocus
                    />
                    <button onClick={() => updateCollection(col.id)} className="text-[#C27054] hover:text-[#A85B42] text-xs font-semibold">Save</button>
                    <button onClick={() => setEditingCollection(null)} className="text-[#7A8A73] hover:text-[#2D3B26] text-xs">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 bg-[#E4E8DF] rounded-full px-4 py-2 text-sm text-[#2D3B26]">
                    <span>{col.name}</span>
                    <button
                      onClick={() => { setEditingCollection(col.id); setEditCollectionName(col.name); }}
                      className="ml-1 text-[#7A8A73] hover:text-[#C27054] transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => deleteCollection(col.id)}
                      className="text-[#7A8A73] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Coupon Manager */}
        <div className="bg-white rounded-2xl p-6 mb-8" data-testid="coupon-manager">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Tag size={18} className="text-[#C27054]" />
              <div>
                <h2 className="text-lg font-serif text-[#2D3B26]">Coupon Codes</h2>
                <p className="text-xs text-[#7A8A73]">Customers apply these at checkout for instant savings.</p>
              </div>
            </div>
          </div>

          {/* Create Coupon Form */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4">
            <input
              type="text"
              value={couponForm.code}
              onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
              data-testid="input-coupon-code"
              className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm uppercase"
              placeholder="CODE (e.g. NOVA20)"
            />
            <select
              value={couponForm.discount_type}
              onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })}
              data-testid="select-coupon-type"
              className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
            >
              <option value="percent">% Off</option>
              <option value="flat">Flat ₹ Off</option>
            </select>
            <input
              type="number"
              value={couponForm.discount_value}
              onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
              data-testid="input-coupon-value"
              className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              placeholder={couponForm.discount_type === 'percent' ? '20' : '500'}
            />
            <input
              type="number"
              value={couponForm.min_order}
              onChange={(e) => setCouponForm({ ...couponForm, min_order: e.target.value })}
              data-testid="input-coupon-min-order"
              className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              placeholder="Min order ₹"
            />
            <input
              type="number"
              value={couponForm.max_discount}
              onChange={(e) => setCouponForm({ ...couponForm, max_discount: e.target.value })}
              data-testid="input-coupon-max-discount"
              className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              placeholder="Max ₹ off"
            />
            <button
              onClick={addCoupon}
              data-testid="add-coupon-button"
              className="px-4 py-2 bg-[#C27054] text-white rounded-lg hover:bg-[#A85B42] transition-colors text-sm font-semibold"
            >
              + Add Coupon
            </button>
          </div>

          {/* Coupon List */}
          {coupons.length === 0 ? (
            <p className="text-xs text-[#7A8A73] italic">No coupons yet. Create one above.</p>
          ) : (
            <div className="space-y-2">
              {coupons.map((c) => (
                <div key={c.id} className="flex items-center justify-between bg-[#f6f7f4] rounded-lg px-4 py-3" data-testid={`coupon-${c.id}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="font-mono font-bold text-[#C27054] text-sm">{c.code}</span>
                    <span className="text-xs text-[#475740]">
                      {c.discount_type === 'percent' ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                    </span>
                    {c.min_order > 0 && <span className="text-xs text-[#7A8A73]">Min ₹{c.min_order}</span>}
                    {c.max_discount && <span className="text-xs text-[#7A8A73]">Cap ₹{c.max_discount}</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? 'bg-[#7A8A73]/20 text-[#2D3B26]' : 'bg-red-100 text-red-700'}`}>
                      {c.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    onClick={() => deleteCoupon(c.id)}
                    data-testid={`delete-coupon-${c.id}`}
                    className="text-[#7A8A73] hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sale Manager */}
        <div className="bg-white rounded-2xl p-6 mb-8" data-testid="sale-manager">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Percent size={18} className="text-[#C27054]" />
              <div>
                <h2 className="text-lg font-serif text-[#2D3B26]">Sitewide Sales</h2>
                <p className="text-xs text-[#7A8A73]">Apply a % discount to a Collection, Category or Jewelry Type.</p>
              </div>
            </div>
          </div>

          {/* Create Sale Form */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4">
            <input
              type="text"
              value={saleForm.name}
              onChange={(e) => setSaleForm({ ...saleForm, name: e.target.value })}
              data-testid="input-sale-name"
              className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              placeholder="Sale name (e.g. Diwali Dhamaka)"
            />
            <select
              value={saleForm.target_type}
              onChange={(e) => setSaleForm({ ...saleForm, target_type: e.target.value, target_value: '' })}
              data-testid="select-sale-target-type"
              className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
            >
              <option value="collection">Collection</option>
              <option value="category">Category</option>
              <option value="jewelry_type">Jewelry Type</option>
              <option value="all">Sitewide (All)</option>
            </select>
            {saleForm.target_type === 'collection' ? (
              <select
                value={saleForm.target_value}
                onChange={(e) => setSaleForm({ ...saleForm, target_value: e.target.value })}
                data-testid="select-sale-target-value"
                className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              >
                <option value="">Select Collection</option>
                {collections.map((c) => <option key={c.id} value={c.slug || c.name}>{c.name}</option>)}
              </select>
            ) : saleForm.target_type === 'category' ? (
              <select
                value={saleForm.target_value}
                onChange={(e) => setSaleForm({ ...saleForm, target_value: e.target.value })}
                data-testid="select-sale-target-value"
                className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              >
                <option value="">Select Category</option>
                <option value="gold">Gold</option>
                <option value="silver">Silver</option>
              </select>
            ) : saleForm.target_type === 'jewelry_type' ? (
              <select
                value={saleForm.target_value}
                onChange={(e) => setSaleForm({ ...saleForm, target_value: e.target.value })}
                data-testid="select-sale-target-value"
                className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              >
                <option value="">Select Type</option>
                <option value="ring">Ring</option>
                <option value="earring">Earring</option>
                <option value="pendant">Pendant</option>
                <option value="necklace">Necklace</option>
                <option value="bracelet">Bracelet</option>
                <option value="mangalsutra">Mangalsutra</option>
                <option value="nosepin">Nosepin</option>
                <option value="chain">Chain</option>
              </select>
            ) : (
              <input
                type="text"
                value="all"
                disabled
                data-testid="select-sale-target-value"
                className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#7A8A73] text-sm"
              />
            )}
            <input
              type="number"
              value={saleForm.discount_percent}
              onChange={(e) => setSaleForm({ ...saleForm, discount_percent: e.target.value })}
              data-testid="input-sale-discount"
              className="px-3 py-2 bg-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
              placeholder="% off (e.g. 15)"
            />
            <button
              onClick={() => {
                if (saleForm.target_type === 'all') {
                  addSale({ target_value: 'all' });
                } else {
                  addSale();
                }
              }}
              data-testid="add-sale-button"
              className="px-4 py-2 bg-[#C27054] text-white rounded-lg hover:bg-[#A85B42] transition-colors text-sm font-semibold"
            >
              + Add Sale
            </button>
          </div>

          {/* Sale List */}
          {sales.length === 0 ? (
            <p className="text-xs text-[#7A8A73] italic">No sales running. Create one above.</p>
          ) : (
            <div className="space-y-2">
              {sales.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-[#f6f7f4] rounded-lg px-4 py-3" data-testid={`sale-${s.id}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="font-semibold text-[#2D3B26] text-sm">{s.name}</span>
                    <span className="text-xs text-[#475740]">{s.discount_percent}% off</span>
                    <span className="text-xs text-[#7A8A73] capitalize">on {s.target_type}: <strong>{s.target_value}</strong></span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.active ? 'bg-[#7A8A73]/20 text-[#2D3B26]' : 'bg-red-100 text-red-700'}`}>
                      {s.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    onClick={() => deleteSale(s.id)}
                    data-testid={`delete-sale-${s.id}`}
                    className="text-[#7A8A73] hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Orders Manager */}
        <div className="bg-white rounded-2xl p-6 mb-8" data-testid="orders-manager">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <ClipboardList size={18} className="text-[#C27054]" />
              <div>
                <h2 className="text-lg font-serif text-[#2D3B26]">Customer Orders</h2>
                <p className="text-xs text-[#7A8A73]">Track orders, gift-wrap requests, and update fulfillment status.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button
                onClick={() => setOrderFilter('all')}
                data-testid="orders-filter-all"
                className={`px-3 py-1.5 rounded-full font-semibold uppercase tracking-wider transition-colors ${orderFilter === 'all' ? 'bg-[#2D3B26] text-white' : 'bg-[#E4E8DF] text-[#475740] hover:bg-[#7A8A73]/30'}`}
              >
                All ({adminOrders.length})
              </button>
              <button
                onClick={() => setOrderFilter('gift')}
                data-testid="orders-filter-gift"
                className={`px-3 py-1.5 rounded-full font-semibold uppercase tracking-wider transition-colors flex items-center gap-1 ${orderFilter === 'gift' ? 'bg-[#C27054] text-white' : 'bg-[#fff5f0] text-[#C27054] hover:bg-[#C27054]/20'}`}
              >
                <Gift size={11} /> Gift ({adminOrders.filter(o => o.gift_wrap).length})
              </button>
              <button
                onClick={() => setOrderFilter('pending')}
                data-testid="orders-filter-pending"
                className={`px-3 py-1.5 rounded-full font-semibold uppercase tracking-wider transition-colors ${orderFilter === 'pending' ? 'bg-[#2D3B26] text-white' : 'bg-[#E4E8DF] text-[#475740] hover:bg-[#7A8A73]/30'}`}
              >
                Pending ({adminOrders.filter(o => o.status === 'pending').length})
              </button>
            </div>
          </div>

          {(() => {
            const filtered = adminOrders.filter(o => {
              if (orderFilter === 'gift') return o.gift_wrap;
              if (orderFilter === 'pending') return o.status === 'pending';
              return true;
            });
            if (filtered.length === 0) {
              return <p className="text-xs text-[#7A8A73] italic py-6 text-center">No orders match this filter.</p>;
            }
            return (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filtered.map((o) => (
                  <div key={o.id} data-testid={`admin-order-${o.id}`} className={`rounded-xl p-4 border ${o.gift_wrap ? 'bg-[#fff5f0] border-[#C27054]/30' : 'bg-[#f6f7f4] border-transparent'}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-mono font-bold text-sm text-[#2D3B26]">#{o.id.slice(0, 8).toUpperCase()}</span>
                          {o.gift_wrap && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-[#C27054] text-white px-2 py-0.5 rounded-full" data-testid={`admin-order-gift-flag-${o.id}`}>
                              <Gift size={10} /> Gift Wrap
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E4E8DF] text-[#475740] font-semibold">
                            {o.status}
                          </span>
                        </div>
                        <p className="text-xs text-[#7A8A73]">
                          {o.buyer_name || 'Customer'} · {o.buyer_email || '—'} · {new Date(o.created_at).toLocaleDateString('en-IN')}
                        </p>
                        {(o.first_name || o.city) && (
                          <p className="text-xs text-[#475740] mt-1">
                            <span className="font-semibold">Ship To:</span> {o.first_name} {o.last_name}{o.city ? `, ${o.city}` : ''}{o.state ? `, ${o.state}` : ''}{o.pincode ? ` - ${o.pincode}` : ''}
                          </p>
                        )}
                        {o.expected_delivery_date && (
                          <p className="text-xs text-[#C27054] mt-1 font-medium" data-testid={`admin-order-delivery-${o.id}`}>
                            ⏰ Deliver by {new Date(o.expected_delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </p>
                        )}
                        <p className="text-xs text-[#475740] mt-1">
                          {o.items?.length || 0} item(s) · <span className="font-semibold text-[#C27054]">₹{Number(o.total_amount).toLocaleString('en-IN')}</span>
                        </p>
                        {o.gift_wrap && o.gift_message && (
                          <p className="mt-2 text-xs italic text-[#475740] bg-white rounded-lg px-3 py-2 border border-[#C27054]/20" data-testid={`admin-order-gift-msg-${o.id}`}>
                            <Gift size={10} className="inline text-[#C27054] mr-1" />
                            "{o.gift_message}"
                          </p>
                        )}
                      </div>
                      <select
                        value={o.status}
                        onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                        data-testid={`admin-order-status-select-${o.id}`}
                        className="text-xs px-3 py-1.5 bg-white border border-[#E4E8DF] rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] cursor-pointer"
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Products Table */}
        {products.length === 0 ? (
          <div className="text-center py-20">
            <Package size={64} className="mx-auto text-[#7A8A73] mb-4" />
            <p className="text-[#7A8A73] text-lg mb-4">No products yet</p>
            <button
              onClick={openAddModal}
              className="text-[#C27054] hover:underline text-sm"
            >
              Add your first product
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl p-4 flex items-center gap-4"
                data-testid={`admin-product-${product.id}`}
              >
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-serif text-[#2D3B26] truncate">{product.name}</h3>
                    {(() => {
                      if (!product.available_date) return null;
                      const listed = new Date(product.available_date);
                      const today = new Date(); today.setHours(0,0,0,0);
                      if (listed > today) {
                        return (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#fff5f0] text-[#C27054] border border-[#C27054]/30 whitespace-nowrap" data-testid={`scheduled-badge-${product.id}`}>
                            ⏰ Scheduled · {listed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <p className="text-xs text-[#7A8A73]">
                    {product.category.toUpperCase()} &middot; {product.metal_type}
                    {product.available_date && (
                      <> &middot; <span className="text-[#475740]">Listed: {new Date(product.available_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></>
                    )}
                  </p>
                </div>

                <p className="text-lg font-medium text-[#C27054] whitespace-nowrap">
                  &#8377;{product.price}
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(product)}
                    data-testid={`edit-product-${product.id}`}
                    className="w-10 h-10 bg-[#E4E8DF] rounded-full flex items-center justify-center hover:bg-[#C27054] hover:text-white transition-colors duration-300"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => deleteProduct(product.id)}
                    data-testid={`delete-product-${product.id}`}
                    className="w-10 h-10 bg-[#E4E8DF] rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors duration-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
              onClick={() => setShowModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3 }}
                className="bg-[#FDFBF7] rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="product-modal"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-serif text-[#2D3B26]">
                    {editingProduct ? 'Edit Product' : 'Add Product'}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    data-testid="close-modal-button"
                    className="w-10 h-10 bg-[#E4E8DF] rounded-full flex items-center justify-center hover:bg-[#C27054] hover:text-white transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-[#2D3B26] mb-2">
                      Product Image
                    </label>
                    <div className="flex gap-3 items-center">
                      {formData.image_url ? (
                        <img
                          src={formData.image_url}
                          alt="Preview"
                          className="w-20 h-20 rounded-xl object-cover"
                          data-testid="image-preview"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-[#E4E8DF] flex items-center justify-center">
                          <Image size={24} className="text-[#7A8A73]" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          data-testid="image-file-input"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          data-testid="upload-image-button"
                          className="w-full bg-[#E4E8DF] text-[#2D3B26] rounded-full px-4 py-2 hover:bg-[#C27054] hover:text-white transition-colors duration-300 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <Upload size={16} />
                          {uploading ? 'Uploading...' : 'Upload Image'}
                        </button>
                        <p className="text-xs text-[#7A8A73] mt-2">or paste URL below</p>
                        <input
                          type="text"
                          name="image_url"
                          value={formData.image_url}
                          onChange={handleChange}
                          data-testid="input-image-url"
                          className="w-full px-3 py-2 bg-[#E4E8DF] border-none rounded-lg text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-xs mt-1"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2D3B26] mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="input-product-name"
                      className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      placeholder="Product name"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-[#2D3B26]">Description *</label>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!formData.name) { toast.error('Enter product name first'); return; }
                          setDescLoading(true);
                          try {
                            const { data } = await axios.post(`${BACKEND_URL}/api/auto-description`, {
                              name: formData.name,
                              category: formData.category,
                              jewelry_type: formData.jewelry_type,
                              metal_type: formData.metal_type,
                              weight: formData.weight,
                            }, { withCredentials: true });
                            setFormData(prev => ({ ...prev, description: data.description }));
                            toast.success('Description generated!');
                          } catch (err) { toast.error('Failed to generate description'); }
                          finally { setDescLoading(false); }
                        }}
                        disabled={descLoading}
                        data-testid="auto-description-button"
                        className="text-xs bg-[#C27054] text-white px-3 py-1 rounded-full hover:bg-[#A85B42] transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {descLoading ? 'Generating...' : 'AI Generate'}
                      </button>
                    </div>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      rows={3}
                      data-testid="input-product-description"
                      className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      placeholder="Product description (or click AI Generate)"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2D3B26] mb-2">Category *</label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        required
                        data-testid="select-product-category"
                        className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      >
                        <option value="gold">Gold</option>
                        <option value="silver">Silver</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2D3B26] mb-2">Jewelry Type *</label>
                      <select
                        name="jewelry_type"
                        value={formData.jewelry_type}
                        onChange={handleChange}
                        data-testid="select-jewelry-type"
                        className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      >
                        <option value="">Select Type</option>
                        <option value="ring">Ring</option>
                        <option value="earring">Earring</option>
                        <option value="pendant">Pendant</option>
                        <option value="necklace">Necklace</option>
                        <option value="bracelet">Bracelet</option>
                        <option value="mangalsutra">Mangalsutra</option>
                        <option value="nosepin">Nosepin</option>
                        <option value="chain">Chain</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2D3B26] mb-2">Collection</label>
                      <select
                        name="collection"
                        value={formData.collection}
                        onChange={handleChange}
                        data-testid="select-collection"
                        className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      >
                        <option value="">Select Collection</option>
                        {collections.map((col) => (
                          <option key={col.id} value={col.slug}>{col.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2D3B26] mb-2">Metal Type *</label>
                    <select
                      name="metal_type"
                      value={formData.metal_type}
                      onChange={handleChange}
                      required
                      data-testid="select-metal-type"
                      className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                    >
                      <option value="">Select Metal</option>
                      <option value="22K Gold">22K Gold</option>
                      <option value="18K Gold">18K Gold</option>
                      <option value="14K Gold">14K Gold</option>
                      <option value="9K Gold">9K Gold</option>
                      <option value="925 Sterling Silver">925 Sterling Silver</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2D3B26] mb-2">Weight (display)</label>
                      <input type="text" name="weight" value={formData.weight} onChange={handleChange}
                        data-testid="input-product-weight"
                        className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                        placeholder="e.g., 12g" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2D3B26] mb-2">Size Guide</label>
                      <input type="text" name="size_guide" value={formData.size_guide} onChange={handleChange}
                        data-testid="input-product-size-guide"
                        className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                        placeholder="e.g., Sizes 5-9" />
                    </div>
                  </div>

                  {/* Auto Price Calculation */}
                  <div className="bg-[#f6f7f4] rounded-xl p-4">
                    <p className="text-xs font-semibold text-[#C27054] uppercase tracking-wider mb-3">Auto Price Calculator</p>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-[#2D3B26] mb-1">Weight (grams) *</label>
                        <input type="number" name="weight_grams" value={formData.weight_grams}
                          onChange={(e) => {
                            handleChange(e);
                            // Auto-fill display weight too
                            if (e.target.value) setFormData(prev => ({ ...prev, weight_grams: e.target.value, weight: `${e.target.value}g` }));
                          }}
                          step="0.01"
                          data-testid="input-weight-grams"
                          className="w-full px-4 py-2 bg-[#E4E8DF] border-none rounded-lg text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
                          placeholder="e.g., 4.5" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#2D3B26] mb-1">Karat</label>
                        <select name="selectedKarat" value={formData.selectedKarat || '22K'}
                          onChange={(e) => setFormData(prev => ({ ...prev, selectedKarat: e.target.value }))}
                          className="w-full px-4 py-2 bg-[#E4E8DF] border-none rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm">
                          <option value="22K">22K</option>
                          <option value="18K">18K</option>
                          <option value="14K">14K</option>
                          <option value="9K">9K</option>
                        </select>
                      </div>
                    </div>
                    <button type="button" onClick={async () => {
                      if (!formData.weight_grams) { toast.error('Enter weight in grams'); return; }
                      try {
                        const { data } = await axios.post(`${BACKEND_URL}/api/auto-price`, {
                          weight_grams: parseFloat(formData.weight_grams),
                          karat: formData.selectedKarat || '22K',
                        }, { withCredentials: true });
                        setFormData(prev => ({ ...prev, price: String(data.price) }));
                        setAutoBreakdown(data.breakdown);
                        toast.success(`Price auto-calculated: \u20b9${data.price.toLocaleString('en-IN')}`);
                      } catch (err) { toast.error('Failed to calculate price'); }
                    }}
                      data-testid="auto-price-button"
                      className="w-full bg-[#2D3B26] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#1a2a1a] transition-colors flex items-center justify-center gap-2">
                      Calculate Price
                    </button>
                    {autoBreakdown && (
                      <div className="mt-3 text-xs space-y-1 border-t border-dashed border-[rgba(45,59,38,0.15)] pt-3">
                        <div className="flex justify-between text-[#475740]">
                          <span>Gold ({autoBreakdown.karat} @ {'\u20b9'}{autoBreakdown.rate_per_gram}/g)</span>
                          <span>{'\u20b9'}{autoBreakdown.gold_value.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between text-[#475740]">
                          <span>Making ({'\u20b9'}{autoBreakdown.making_per_gram}/g x {autoBreakdown.weight_grams}g)</span>
                          <span>{'\u20b9'}{autoBreakdown.making_total.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between font-bold text-[#2D3B26]">
                          <span>Total Price</span>
                          <span>{'\u20b9'}{autoBreakdown.total.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    )}
                    <input type="hidden" name="price" value={formData.price} />
                  </div>

                  {/* Listing Date + Stock */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#2D3B26] mb-1 uppercase tracking-wider">Listing Date</label>
                      <input
                        type="date"
                        name="available_date"
                        value={formData.available_date}
                        onChange={handleChange}
                        data-testid="input-available-date"
                        className="w-full px-4 py-2 bg-[#E4E8DF] border-none rounded-lg text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
                      />
                      <p className="text-[10px] text-[#7A8A73] mt-1">Future date schedules the product. Past/today = listed now.</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#2D3B26] mb-1 uppercase tracking-wider">Stock Status</label>
                      <div className="flex items-center gap-3 px-4 py-2 bg-[#E4E8DF] rounded-lg">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.in_stock !== false}
                            onChange={(e) => setFormData(prev => ({ ...prev, in_stock: e.target.checked }))}
                            data-testid="input-in-stock"
                            className="w-4 h-4 accent-[#C27054]"
                          />
                          <span className="text-sm text-[#2D3B26]">{formData.in_stock !== false ? 'In Stock' : 'Out of Stock'}</span>
                        </label>
                      </div>
                      <p className="text-[10px] text-[#7A8A73] mt-1">Untick to show "Out of Stock" + Notify Me button on the product page.</p>
                    </div>
                  </div>

                  {/* Per-Product Discount */}
                  <div className="bg-[#fff5f0] rounded-xl p-4 border border-[#C27054]/20">
                    <p className="text-xs font-semibold text-[#C27054] uppercase tracking-wider mb-3 flex items-center gap-1">
                      <Tag size={12} /> Product-Level Discount (Optional)
                    </p>
                    <div className="grid grid-cols-2 gap-4 items-end">
                      <div>
                        <label className="block text-xs font-medium text-[#2D3B26] mb-1">Discount %</label>
                        <input
                          type="number"
                          name="discount_percent"
                          value={formData.discount_percent}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          max="90"
                          data-testid="input-product-discount"
                          className="w-full px-4 py-2 bg-white border border-[#E4E8DF] rounded-lg text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
                          placeholder="e.g., 10"
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!formData.discount_active}
                          onChange={(e) => setFormData(prev => ({ ...prev, discount_active: e.target.checked }))}
                          data-testid="input-product-discount-active"
                          className="w-4 h-4 accent-[#C27054]"
                        />
                        <span className="text-sm text-[#2D3B26]">Activate discount</span>
                      </label>
                    </div>
                    <p className="text-[10px] text-[#7A8A73] mt-2">Applies to this product only. Sitewide sales stack on top of this.</p>
                  </div>

                  <button
                    type="submit"
                    data-testid="save-product-button"
                    className="w-full bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold"
                  >
                    {editingProduct ? 'Update Product' : 'Add Product'}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminPanel;
